/**
 * @fileoverview Canonical ecosystem-wide SSOT for language support.
 *
 * Every Colony app consumes `SUPPORTED_LANGUAGES` + `DEFAULT_LANGUAGE`
 * + `STORAGE_KEY` from this module via `@puffin-core/i18n-bootstrap`.
 * Drift is caught by the I18N-LANG-001 cross-app contract test that
 * walks every apps/<app>/src tree and forbids local `LANGUAGE_OPTIONS`
 * constants.
 *
 * Adding a new language requires:
 *   1. Author the bundle (en.json + es.json + new ${code}.json + paired _meta.json)
 *   2. Add its code to `BUNDLED_LANGUAGE_CODES` (`../readingLanguage.js`, which every entry's
 *      `hasBundle` is derived from) and flip its `comingSoon` to false
 *   3. Update the cross-app contract assertion that locks the count
 *   4. Update the per-app PreferencesPage tests
 *
 * @module @puffin-core/i18n-bootstrap/constants
 */

import { BUNDLED_LANGUAGE_CODES, DEFAULT_LANGUAGE } from './readingLanguage.js';

// Each entry's `hasBundle` is read from the ONE bundled list a server reads too (PAYDAY-UAT-REMEDIATION-001 U8 W8-5),
// never a second flag beside it.

/**
 * Frozen array of every language this resolver will boot into.
 *
 * `advertised:false` entries are **deliberately absent from the picker** —
 * see `en-XA` below — so "supported" here means "resolvable", not
 * "offered". `buildLanguageOptionsForLocale` filters on `advertised`;
 * `getInitialLanguage` / `changeLanguage` do not.
 *
 * Entries with `hasBundle:false` render as `<option disabled>` with a
 * "Coming soon" suffix via `buildLanguageOptionsForLocale`.
 *
 * `dir` is the writing direction the resolver applies to `<html dir>`
 * (`useSyncHtmlLang`). It lives here because direction is a property OF THE
 * LANGUAGE — deriving it anywhere else is how you end up with a second
 * writer racing the first.
 *
 * @type {ReadonlyArray<Readonly<{
 *   code: string,
 *   label: string,
 *   hasBundle: boolean,
 *   comingSoon: boolean,
 *   dir: 'ltr'|'rtl',
 *   advertised: boolean,
 * }>>}
 */
export const SUPPORTED_LANGUAGES = Object.freeze([
  Object.freeze({
    code: 'en',
    label: 'English',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('en'),
    comingSoon: false,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'es',
    label: 'Español',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('es'),
    comingSoon: false,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'fr',
    label: 'Français',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('fr'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'de',
    label: 'Deutsch',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('de'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'pt',
    label: 'Português',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('pt'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'ja',
    label: '日本語',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('ja'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'ko',
    label: '한국어',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('ko'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  Object.freeze({
    code: 'zh',
    label: '中文',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('zh'),
    comingSoon: true,
    dir: 'ltr',
    advertised: true,
  }),
  // ── AAS-I18N-002 ruling 11 (Q-F3-P4, 2026-08-10) ─────────────────────────
  // `en-XA` is a PSEUDO-LOCALE: a real, resolvable locale that is
  // deliberately never offered in the picker. Standard practice — Microsoft
  // and Google ship `en-XA` / `ar-XB` exactly this way.
  //
  // 🔴 What it replaces, and why the replacement is not a compromise:
  // `@puffin-core/page-shell` has shipped a 285-leaf pseudo-locale string
  // bundle since 2026-05-07, held in lockstep with `en`/`es` by a parity test,
  // and it has NEVER RENDERED A SINGLE CHARACTER. Two independent reasons, and
  // the second was only found by running it:
  //   1. The code was not in this array, so nothing could boot it. Its
  //      documented workflow had the DEVELOPER set `document.dir = 'rtl'` BY
  //      HAND — a second code path that rots unobserved and proves nothing
  //      about the chain real users traverse. (Same objection that retired the
  //      mirrored CLDR helpers under ruling 19.)
  //   2. 🔴 The code itself was unusable. It was `rtl-test`, and **i18next
  //      upper-cases the second subtag** (`formatLanguageCode`), so the
  //      resolution hierarchy read `['rtl-Test','rtl','en']` and a bundle
  //      registered under the literal `'rtl-test'` was never matched. MEASURED
  //      against real i18next: `t()` returned ENGLISH while
  //      `hasResourceBundle('rtl-test','translation')` returned true.
  //
  // Why `en-XA` and not `ar-XB` (the standard *bidi* pseudo-locale):
  // `Intl.PluralRules('ar-XB')` reports SIX categories (zero, one, two, few,
  // many, other). The app half of this locale is derived mechanically from
  // English, which has `one`/`other` only, so under `ar-XB` any count in the
  // other four categories would render a RAW DOTTED KEY. `en-XA` reports
  // `one, other` — faithful to its source. Direction comes from `dir` below,
  // not from the tag.
  //
  // Availability is controlled by the PICKER — `advertised:false` — never by
  // `NODE_ENV`, of which this package contains not one branch.
  //
  // Consequences that are the point, not side effects:
  //   · `dir` is now derived from locale metadata by the SAME resolver that
  //     picks the language, so a real RTL locale (ar/he) needs no new
  //     mechanism — just a row here with `dir:'rtl'`.
  //   · page-shell's three-bundle lockstep becomes LOAD-BEARING: the parity
  //     test now guards a bundle something can actually render.
  //
  // ⚠️ `@puffin-core/auth-routes`' `userPreferences` validator
  // (`/^[a-z]{2}(-[A-Z]{2})?$/`) DOES accept `en-XA` — verified, and worth
  // stating because the old `rtl-test` code did not, which made an earlier
  // draft of this note claim the opposite. So a server preference CAN hold it;
  // what cannot happen is a user REACHING it, because no picker offers it.
  // A pseudo-locale is therefore always a client-side selection, and the
  // preference bridges treat an UNADVERTISED resolved language as an explicit
  // override the server value must not clobber (see `useApplyServerLanguage`).
  // Without that rule it would survive ~1s on exactly the authenticated chrome
  // it exists to test.
  Object.freeze({
    code: 'en-XA',
    label: 'Pseudo-RTL (QA)',
    hasBundle: BUNDLED_LANGUAGE_CODES.includes('en-XA'),
    comingSoon: false,
    dir: 'rtl',
    advertised: false,
  }),
]);

/**
 * Flat array of every RESOLVABLE code — what `getInitialLanguage`,
 * `changeLanguage` and the preference bridges validate against. Includes
 * unadvertised pseudo-locales; use `ADVERTISED_LANGUAGE_CODES` for anything
 * user-facing.
 *
 * Provided as a separate frozen constant because consumers that only
 * need code validation (e.g. JWT claim sanitization) shouldn't have to
 * map across the structured array.
 *
 * @type {ReadonlyArray<string>}
 */
export const SUPPORTED_LANGUAGE_CODES = Object.freeze(SUPPORTED_LANGUAGES.map((e) => e.code));

/**
 * The codes a USER may be offered. This — not `SUPPORTED_LANGUAGE_CODES` —
 * is what a picker, a marketing language switcher or a "which languages do
 * we ship" claim should read.
 *
 * @type {ReadonlyArray<string>}
 */
export const ADVERTISED_LANGUAGE_CODES = Object.freeze(
  SUPPORTED_LANGUAGES.filter((e) => e.advertised).map((e) => e.code),
);

/**
 * Default language code used when no other signal is available
 * (no JWT claim, no storage cache, no navigator language match).
 *
 * Must always point to an entry with `hasBundle:true` — defaulting to
 * an un-shipped language would render every UI string as the i18next
 * key path, which is worse than EN-fallback.
 *
 * @type {string}
 */
export { DEFAULT_LANGUAGE };

/**
 * Canonical localStorage / AsyncStorage key for the cached language
 * preference. Deliberately NOT app-prefixed: language is a user
 * property, not an app property. Sharing one key across all tabs of an
 * origin gives free cross-tab sync via the `storage` event.
 *
 * Deliberately NOT platform-branded either (LIFE-SHORTLISTED-001 P5): a
 * white-label tenant site writes this key in every visitor's browser
 * before any consent (it is strictly necessary storage, disclosed as
 * such), and the platform's name must not appear in a tenant's storage.
 *
 * Old keys (`planner-language`, `prep-language`, `palm_language`, and
 * `puffin_i18n_language`, renamed 2026-09-29) are inert: the factory reads
 * and writes only this key. There is no migration shim (Gen 0: no legacy
 * support). A browser that cached a choice under the old name resolves its
 * language from the account, the tenant or the browser once, then caches it
 * here.
 *
 * @type {string}
 */
export const STORAGE_KEY = 'i18n_language';
